<script setup lang="ts">
import { computed } from 'vue';

import { EntityPresenterV2Mode } from '@satellite/composables/presenterV2EntityMode';
import { useYardAreaPresenterV2 } from '@satellite/composables/useYardAreaPresenterV2';
import { useYardSpotFetcherV2 } from '@satellite/composables/useYardSpotFetcherV2';
import { useYardSpotDisplayV2 } from '@satellite/composables/useYardSpotDisplayV2';
import YardSpotDisplayV2 from '@satellite/components/elements/presenters/YardSpotDisplayV2.vue';

import type { Maybe } from '@nova/core';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterV2Placeholder';
import type { YardAreaDetails } from '@satellite/composables/useYardAreaPresenterV2';
import type { YardSpotDetails } from '@satellite/composables/useYardSpotPresenterV2';
import type { YardSpotDisplayField } from '@satellite/composables/useYardSpotDisplayV2';

const {
  size = 'regular',
  spotId = null,
  initialSpotData = null,
  placeholderSpotData = null,
  staticSpotData = null,
  initialAreaData = null,
  placeholderAreaData = null,
  staticAreaData = null,
  mode = EntityPresenterV2Mode.Live,
  displayFields = ['area', 'code', 'spotIcon'],
  areaProps = {},
  spotCodeProps = {},
  spotIconProps = {},
  spotStatusProps = {}
} = defineProps<{
  size?: 'regular' | 'small' | 'large';
  spotId?: Maybe<string>;
  initialSpotData?: Maybe<YardSpotDetails>;
  placeholderSpotData?: Maybe<PresenterPlaceholderData<YardSpotDetails>>;
  staticSpotData?: Maybe<YardSpotDetails>;
  initialAreaData?: Maybe<YardAreaDetails>;
  placeholderAreaData?: Maybe<PresenterPlaceholderData<YardAreaDetails>>;
  staticAreaData?: Maybe<YardAreaDetails>;
  mode?: EntityPresenterV2Mode;
  displayFields?: YardSpotDisplayField[];
  areaProps?: Record<string, unknown>;
  spotCodeProps?: Record<string, unknown>;
  spotIconProps?: Record<string, unknown>;
  spotStatusProps?: Record<string, unknown>;
}>();

const needsArea = computed(() => displayFields.includes('area'));

const {
  data: spot,
  isLoading,
  isFetching,
  onIntersect: onSpotIntersect
} = useYardSpotFetcherV2({
  spotId: computed(() => spotId),
  initialSpotData: computed(() => initialSpotData),
  placeholderSpotData: computed(() => placeholderSpotData),
  staticSpotData: computed(() => staticSpotData),
  mode
});

const areaId = computed(() => (needsArea.value ? spot.value?.spotAreaId ?? null : null));

const {
  data: area,
  isFetching: isAreaFetching,
  onIntersect: onAreaIntersect
} = useYardAreaPresenterV2({
  areaId,
  initialAreaData: computed(() => initialAreaData),
  placeholderAreaData: computed(() => placeholderAreaData),
  staticAreaData: computed(() => staticAreaData),
  mode
});

const onIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
  onSpotIntersect(entry);
  if (needsArea.value) {
    onAreaIntersect(entry);
  }
};

const data = useYardSpotDisplayV2(spot, {
  area,
  size: computed(() => size),
  mode: computed(() => mode),
  isFetching,
  isAreaFetching,
  initialAreaData: computed(() => initialAreaData),
  placeholderAreaData: computed(() => placeholderAreaData),
  staticAreaData: computed(() => staticAreaData),
  areaProps: computed(() => areaProps),
  codeProps: computed(() => spotCodeProps),
  spotIconProps: computed(() => spotIconProps),
  spotStatusProps: computed(() => spotStatusProps)
});
</script>

<template>
  <v-skeleton-loader
    :loading="isLoading"
    type="text"
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.5]
      }
    }"
    class="yard-spot-presenter-v2 bg-transparent">
    <yard-spot-display-v2
      :data="data"
      :display-fields="displayFields"
      :style="isLoading ? 'min-width: 50px' : ''">
      <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
        <slot :name="slotName" v-bind="slotProps ?? {}" />
      </template>
    </yard-spot-display-v2>
  </v-skeleton-loader>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  margin: 0 auto !important;
  height: 1em !important;
}
</style>
